import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Search, AlertTriangle, Package } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function CurrentStock() {
  const [stock, setStock] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStock();
  }, []);

  const fetchStock = async (search = '') => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = search ? { search } : {};
      const response = await axios.get(`${BACKEND_URL}/api/current-stock`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setStock(response.data);
    } catch (error) {
      toast.error('Failed to fetch stock');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    fetchStock(query);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-4xl font-extrabold text-slate-900 tracking-tight font-heading" data-testid="current-stock-title">Current Stock</h1>
        <p className="text-slate-600 mt-2">View available inventory levels</p>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
        <Input
          type="text"
          placeholder="Search by item name or category..."
          value={searchQuery}
          onChange={handleSearch}
          data-testid="stock-search-input"
          className="pl-10 border-slate-200"
        />
      </div>

      <Card className="bg-white border-slate-200 shadow-sm">
        <CardContent className="p-0">
          <div className="rounded-xl border border-slate-200 overflow-hidden">
            <table className="w-full" data-testid="stock-table">
              <thead className="bg-slate-50">
                <tr className="border-b border-slate-200">
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Item Name</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Total Received</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Total Issued</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Current Stock</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody>
                {stock.map((item) => (
                  <tr key={item.item_id} className="hover:bg-slate-50/50 transition-colors border-b border-slate-100 last:border-0" data-testid={`stock-row-${item.item_id}`}>
                    <td className="px-6 py-4 text-sm font-medium text-slate-900">{item.item_name}</td>
                    <td className="px-6 py-4 text-sm text-slate-600">
                      <Badge className="bg-slate-100 text-slate-700 border-slate-200">{item.category}</Badge>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600">{item.total_received.toFixed(2)} {item.unit}</td>
                    <td className="px-6 py-4 text-sm text-slate-600">{item.total_issued.toFixed(2)} {item.unit}</td>
                    <td className="px-6 py-4 text-sm font-semibold text-slate-900">{item.current_stock.toFixed(2)} {item.unit}</td>
                    <td className="px-6 py-4 text-sm">
                      {item.is_low_stock ? (
                        <Badge className="bg-red-50 text-red-700 border-red-200 flex items-center gap-1 w-fit" data-testid={`low-stock-badge-${item.item_id}`}>
                          <AlertTriangle className="w-3 h-3" />
                          Low Stock
                        </Badge>
                      ) : (
                        <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 flex items-center gap-1 w-fit">
                          <Package className="w-3 h-3" />
                          In Stock
                        </Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {loading && (
              <div className="p-8 text-center text-slate-600">
                Loading stock data...
              </div>
            )}
            {!loading && stock.length === 0 && (
              <div className="p-8 text-center text-slate-600">
                No stock data available
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default CurrentStock;